/**
 * UC3 launch updates (client, 8 Oct 2026): what the UC3 stepper puts on the map at each
 * step, as pure functions so the mapping is testable without a map.
 *
 * * `rasterForStep` — the one raster a step switches on and the legend title it carries:
 *   since 12 Oct 2026 (NOTES #759) the Crop step shows the crop's GGCP10 production grid
 *   for the production year (`gcp_<crop>_<year>`, the loader's pinned per-year row), or the
 *   GAEZ 2020 production for sorghum and millet, which the grid does not cover; Productivity
 *   shows the crop's area (`mask_<crop>`) behind the survey terciles, swapped for the
 *   modelled yield or the yield gap when the reader picks that measure; the Relationships
 *   step's factor (`'factor'`) is NDVI, rainfall or the soil layer. Every one of them is
 *   clipped to the isolated geography by `IntelIsolate`, which is why nothing here names a
 *   clip.
 * * `productionYear` / `seasonYear`: the year rule the backend applies
 *   (`apps.intelligence.uc3_years`), so the map paints the year the numbers are for.
 * * `yieldTerciles` — the survey yield of every ranked watershed, split into thirds by rank
 *   (low, medium, high). The productivity map paints the watersheds inside the selection
 *   with these classes; the analysis and the results never read them.
 */
import type { IntelUc3Crop } from '../../lib/api';

/** `#uc3Measure`: presentation only, the diagnosis always uses the survey yield. */
export const UC3_MEASURES = ['survey', 'modelled', 'gap'] as const;
export type Uc3Measure = (typeof UC3_MEASURES)[number];

export type Tercile = 'low' | 'medium' | 'high';
export const TERCILES: readonly Tercile[] = ['low', 'medium', 'high'];
/**
 * Low to high yield, amber to green: a different hue family from the crop-area raster's
 * greens underneath, so the classes read over it.
 */
export const TERCILE_COLORS: Record<Tercile, string> = {
  low: '#D9822B',
  medium: '#E9D37A',
  high: '#4E9A3F',
};

export interface Uc3RasterChoice {
  layerId: string;
  /** i18n key under `intel.uc3.layer.*` for the legend title. */
  titleKey: string;
}

export interface Uc3RasterInputs {
  crop: Pick<IntelUc3Crop, 'id' | 'mask' | 'gaez'> | undefined;
  measure: Uc3Measure;
  ndviLayerId: string;
  rainfallLayerId: string;
  soilLayer: string;
  /** The Relationships step's factor (`#uc3LbrFactor`). */
  factor?: 'ndvi' | 'rain' | 'soil';
  /** The production year read (`productionYear`), for the Crop step's grid layer. */
  productionYear?: number;
}

/** UC3 crop id -> its GGCP10 production grid (sorghum and millet have none). */
export const GGCP10_LAYER: Readonly<Record<string, string>> = {
  maize: 'gcp_maize',
  rice: 'gcp_rice',
  soy: 'gcp_soybean',
};

/** The years GGCP10 covers (`apps.datasets.seed.geocropprod`). */
export const PRODUCTION_YEARS: readonly [number, number] = [2010, 2020];

/** The production year read for a chosen year: inside 2010 to 2020, else the nearer end. */
export function productionYear(year: number, range: readonly [number, number] = PRODUCTION_YEARS): number {
  return Math.min(range[1], Math.max(range[0], year));
}

/** The NDVI season read: the chosen year, or the archive's first year for an earlier one. */
export function seasonYear(year: number, firstNdviYear: number | null | undefined): number {
  return firstNdviYear != null && year < firstNdviYear ? firstNdviYear : year;
}

/** The Crop step's layer: the crop's GGCP10 grid for the year, or GAEZ 2020 production. */
export function productionRaster(
  crop: Uc3RasterInputs['crop'],
  year: number | undefined,
): Uc3RasterChoice | null {
  if (!crop) return null;
  const grid = GGCP10_LAYER[crop.id];
  if (grid && year !== undefined) return { layerId: `${grid}_${year}`, titleKey: 'intel.uc3.layer.grid' };
  return { layerId: `gaez_prd_${crop.id}`, titleKey: 'intel.uc3.layer.production' };
}

/** The crop's own area layer: its mask, or its 2020 production where it has none. */
export function cropAreaRaster(crop: Uc3RasterInputs['crop']): Uc3RasterChoice | null {
  if (!crop) return null;
  if (crop.mask) return { layerId: crop.mask, titleKey: 'intel.uc3.layer.mask' };
  return { layerId: `gaez_prd_${crop.id}`, titleKey: 'intel.uc3.layer.production' };
}

/** The raster a step id switches on, or `null` for a step that draws none. */
export function rasterForStep(stepId: string, inputs: Uc3RasterInputs): Uc3RasterChoice | null {
  switch (stepId) {
    case 'crop':
      return productionRaster(inputs.crop, inputs.productionYear);
    case 'indicator':
      if (!inputs.crop) return null;
      if (inputs.measure === 'modelled') {
        return { layerId: inputs.crop.gaez ?? `gaez_yld_${inputs.crop.id}`, titleKey: 'intel.uc3.layer.gaez' };
      }
      if (inputs.measure === 'gap') {
        return { layerId: `gaez_gap_${inputs.crop.id}`, titleKey: 'intel.uc3.layer.gaez_gap' };
      }
      return cropAreaRaster(inputs.crop);
    case 'factor':
      if (inputs.factor === 'ndvi') return { layerId: inputs.ndviLayerId, titleKey: '' };
      if (inputs.factor === 'rain') return { layerId: inputs.rainfallLayerId, titleKey: '' };
      if (inputs.factor === 'soil') return { layerId: inputs.soilLayer, titleKey: `intel.uc3.layer.${inputs.soilLayer}` };
      return null;
    default:
      return null;
  }
}

/**
 * Rank thirds of the survey yield across every ranked watershed: the lowest third `low`,
 * the highest third `high`. Ties keep their rank order; a unit with no yield is left out.
 */
export function yieldTerciles(rows: readonly { unit_id: string; yield?: number | null }[]): Map<string, Tercile> {
  const valued = rows
    .filter((r): r is { unit_id: string; yield: number } => typeof r.yield === 'number' && Number.isFinite(r.yield))
    .sort((a, b) => a.yield - b.yield);
  const out = new Map<string, Tercile>();
  const n = valued.length;
  valued.forEach((row, i) => {
    out.set(row.unit_id, TERCILES[Math.min(2, Math.floor((i * 3) / n))]!);
  });
  return out;
}
